import { useMutation } from "@tanstack/react-query"
import dayjs from "dayjs"
import { InfoIcon } from "lucide-react"
import { parseAsBoolean, parseAsString, useQueryState } from "nuqs"
import { memo, useEffect, useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { fetchNameByBankCode, sendCodeVerify } from "@/api"
import { BottomSheetModal } from "@/components/shared"
import { Button, Input, Switch } from "@/components/ui"
import { AutoCompleteBank } from "@/features/earns/components/banks/bank-autocomplete"
import { ConfirmOTPBank } from "@/features/earns/components/banks/otp-confirm"
import { EValidateUser } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared/use-current-user"
import { formatPhoneNumber, sendHaptic } from "@/modules/helpers/utils"
import type { BankAccount } from "@/modules/types"

const OTP_EXPIRY_SECONDS = 90
const OTP_STORAGE_KEY = "@expired_time_otp_bank"

interface ModalAddAccountBankProps {
  onClose: () => void
  visible: boolean
}

export const AddBank: React.FC<ModalAddAccountBankProps> = memo(({ onClose, visible }) => {
  const [isConfirmOTP, setIsConfirmOTP] = useQueryState(
    "isConfirmOTPAddBankModal",
    parseAsBoolean.withDefault(false)
  )
  const { user } = useCurrentUser()

  const [accountNumber, setAccountNumber] = useQueryState(
    "accountNumber",
    parseAsString.withDefault("")
  )
  const [accountHolderName, setAccountHolderName] = useQueryState(
    "accountHolderName",
    parseAsString.withDefault("")
  )

  const [bankId, setBankId] = useQueryState("bankId", parseAsString.withDefault(""))
  const [bankCode, setBankCode] = useQueryState("bankCode", parseAsString.withDefault(""))
  const debounce = useRef<NodeJS.Timeout | null>(null)

  const form = useForm<{
    accountNumber: string
    holderName: string
    bank: BankAccount
    isDefault: boolean
  }>({
    defaultValues: {
      accountNumber: "",
      holderName: "",
      bank: { id: undefined, code: undefined },
      isDefault: false
    }
  })

  const { mutate: sendCodeVerifyMutation } = useMutation({
    mutationFn: () => sendCodeVerify(),
    mutationKey: ["send_code_verify"],
    onSuccess() {
      localStorage.setItem(
        OTP_STORAGE_KEY,
        dayjs().add(OTP_EXPIRY_SECONDS, "seconds").toISOString()
      )
      const formData = form.getValues()
      setAccountNumber(formData.accountNumber)
      setAccountHolderName(formData.holderName)
      setBankId(formData.bank.id)
      setBankCode(formData.bank.code?.toString())
      setIsConfirmOTP(true)
    },
    onError(error: any) {
      if (
        error?.response?.data?.error?.type?.includes(EValidateUser.YOU_CAN_ONLY_SEND_OTP_AFTER_60S)
      ) {
        setIsConfirmOTP(false)
        toast.error("Bạn chỉ có thể gửi mã xác minh sau 60 giây.")
      }
      if (error?.response?.data?.error?.type?.includes(EValidateUser.SEND_OTP_ZALO_FAILED)) {
        setIsConfirmOTP(false)
        toast.error(
          `Gửi OTP qua Zalo ${formatPhoneNumber(
            user?.phoneNumber || ""
          )} thất bại. Vui lòng thử lại sau.`
        )
        return
      }
      toast.error("Không thể gửi mã xác minh. Vui lòng thử lại.")
    }
  })

  const { mutate: getBankNameByAccountNumber, isPending: isPendingGetAccountName } = useMutation({
    mutationFn: (data: { accountNumber: string; bankCode: number }) => fetchNameByBankCode(data),
    mutationKey: ["get_bank_name"],
    onSuccess(data) {
      if (data.data?.data?.name) {
        form.setValue("holderName", data.data.data.name)
      } else {
        form.setValue("holderName", "")
      }
    },
    onError() {
      form.setValue("holderName", "")
      form.setError("holderName", { message: "Không tìm thấy tên chủ tài khoản" })
    }
  })

  const onSubmit = () => {
    if (debounce.current) clearTimeout(debounce.current)

    debounce.current = setTimeout(() => {
      sendCodeVerifyMutation()
      sendHaptic()
    }, 100)
  }

  const onDebounceSearchBankName = () => {
    if (debounce.current) clearTimeout(debounce.current)

    debounce.current = setTimeout(() => {
      const bank = form.getValues("bank")
      if (bank?.code) {
        form.setValue("holderName", "")
        form.clearErrors()
        getBankNameByAccountNumber({
          accountNumber: form.getValues("accountNumber"),
          bankCode: bank?.code
        })
      }
    }, 500)
  }

  useEffect(() => {
    if (!visible) {
      setAccountHolderName("")
      setAccountNumber("")
      setBankId("")
      setBankCode("")
      setIsConfirmOTP(false)
      sendHaptic()
    }
  }, [visible, setAccountHolderName, setAccountNumber, setBankCode, setBankId, setIsConfirmOTP])

  return (
    <BottomSheetModal isVisible={visible} setIsVisible={onClose} title="Thêm tài khoản">
      <div className="flex flex-col p-2 gap-4">
        <div className="flex flex-col gap-4 p-4 bg-gradient-primary rounded-2xl">
          <ConfirmOTPBank
            onSuccess={() => {
              setIsConfirmOTP(false)
              setTimeout(() => {
                onClose()
                // toast.success("Đã thêm tài khoản ngân hàng thành công")
              }, 800)
            }}
            data={{
              bankId: bankId,
              accountNumber: accountNumber,
              accountHolderName: accountHolderName,
              bankCode: bankCode,
              isDefault: form.getValues("isDefault")
            }}
            visible={isConfirmOTP}
            onClose={() => setIsConfirmOTP(false)}
          />

          {/* Chọn ngân hàng */}
          <Controller
            name="bank"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1 w-full border-b pb-3">
                <AutoCompleteBank
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value)
                    form.setValue("holderName", "")
                    if (form.getValues("accountNumber")) {
                      onDebounceSearchBankName()
                    }
                  }}
                />
                {fieldState.error && (
                  <p className="text-xs text-white">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />

          {/* Nhập số tài khoản */}
          <Controller
            name="accountNumber"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1">
                <Input
                  {...field}
                  placeholder="Nhập vào số tài khoản"
                  type="text"
                  className="text-xs"
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    onDebounceSearchBankName()
                  }}
                />
                {fieldState.error && (
                  <p className="text-xs text-white">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />

          {/* Tên chủ tài khoản */}
          <Controller
            name="holderName"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1">
                <Input
                  value={field.value}
                  className="text-xs bg-gray-200"
                  placeholder={isPendingGetAccountName ? "Đang tìm..." : "Tên chủ tài khoản"}
                  readOnly
                />
                {fieldState.error && (
                  <p className="text-xs text-white">{fieldState.error.message}</p>
                )}
              </div>
            )}
          />
          <Controller
            name="isDefault"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center space-x-2 justify-between">
                <label htmlFor="is-default-bank" className="text-xs font-medium text-white">
                  Đặt làm mặc định
                </label>
                <Switch
                  id="is-default-bank"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </div>
            )}
          />
        </div>
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2 bg-background rounded-lg p-3">
            <InfoIcon className="w-4 h-4 text-primary" />
            <p className="text-xs text-black font-light">
              Đảm bảo thông tin tài khoản của bạn chính xác là thông tin đã đăng ký tại ngân hàng
            </p>
          </div>
        </div>
        <Controller
          name="holderName"
          control={form.control}
          render={({ field }) => (
            <Button
              className="w-full h-12 rounded-xl bg-primary hover:opacity-90"
              onClick={form.handleSubmit(onSubmit)}
              disabled={!field.value || isConfirmOTP}
            >
              Lưu thông tin tài khoản
            </Button>
          )}
        />
      </div>
    </BottomSheetModal>
  )
})
AddBank.displayName = "AddBank"
